import { isBrowser } from './browserHelper';
import { getQueryParamsFromURL } from './urlHelper';

export interface PartnerParams {
  consentGiven?: boolean;
  refApp?: string;
  plannerId?: string;
  projectId?: string;
  imagination?: string;
  eventAction?: string;
}

const NO_REFAPP = 'NO_REFAPP';
const PERFORMANCE_COOKIE_LEVEL = 2;

const parentHasConsent = () => {
  try {
    return window.parent.ikea?.cookieConsent?.hasConsent(
      PERFORMANCE_COOKIE_LEVEL,
    );
  } catch {
    return false;
  }
};

export const hasPerformanceConsent = (): boolean => {
  if (!isBrowser()) return false;
  try {
    return (
      window.ikea?.cookieConsent?.hasConsent(PERFORMANCE_COOKIE_LEVEL) ??
      parentHasConsent() ??
      false
    );
  } catch {
    return parentHasConsent() ?? false;
  }
};

/**
 * Builds a structured partner params object from URL parameters.
 * Applies cookie consent rules:
 * - If no partner params are provided in the URL, returns an empty object.
 * - consentGiven: included whenever any partner param is present.
 * - plannerId, projectId, imagination: always included (strictly necessary).
 * - refApp, eventAction: only included if performance consent is granted.
 */
export const buildPartnerParams = (): PartnerParams => {
  const { vpc, projectId, imagination, refApp, eventAction } =
    getQueryParamsFromURL();

  const hasAnyUrlParams = !!(
    refApp ||
    eventAction ||
    vpc ||
    projectId ||
    imagination
  );

  if (!hasAnyUrlParams) return {};

  const performanceConsent = hasPerformanceConsent();

  const params: PartnerParams = {
    consentGiven: performanceConsent,
    ...(performanceConsent && { refApp: refApp || NO_REFAPP }),
    ...(vpc && { plannerId: vpc }),
    ...(projectId && { projectId }),
    ...(imagination && { imagination }),
    ...(performanceConsent && eventAction && { eventAction }),
  };

  return params;
};
